import classnames from 'classnames';
import type { IFieldProps } from '../elements/Field';
import type { FormContextProps } from '../elements/formContext';
import { Feedback } from '../elements/common';
import { Field } from '../elements/Field';
import { FormContextConsumer } from '../elements/formContext';

export interface ISelectOption<T extends string | number> {
    text: string;
    value: T;
}

interface IDropdownInputProps<T extends string | number> {
    automation?: string;
    customSelectStyles?: React.CSSProperties;
    disabled?: boolean;
    error?: string;
    name?: string;
    newstyles?: boolean;
    onChange: (value: T | null) => void;
    onDoValidate?: (value: T | null) => void;
    placeholder?: string;
    selectOptions: ISelectOption<T>[];
    tabIndex?: number;
    value: T | null | undefined;
}

export function DropdownInput<T extends string | number>(props: FormContextProps<IDropdownInputProps<T>>) {
    return (
        <FormContextConsumer {...props}>
            {(properties) => {
                const noSelection = !properties.value;
                const selectClassName = classnames(
                    'form-control form-dropdown',
                    props.newstyles && 'form-dropdown-ds',
                    props.automation ? 'automation-' + props.automation : '',
                    { 'form-control-danger': !!properties.error, pristine: noSelection }
                );

                return (
                    <>
                        <select
                            className={selectClassName}
                            disabled={properties.disabled}
                            key="select"
                            name={properties.name}
                            onChange={async (e) => {
                                let value: T | null = null;
                                if (typeof properties.value === 'number') {
                                    value = e.target.value === '' ? null : (parseInt(e.target.value, 10) as T);
                                } else {
                                    value = e.target.value === '' ? null : (e.target.value as T);
                                }

                                await properties.onChange(value);

                                if (properties.onDoValidate) {
                                    await properties.onDoValidate(value);
                                }
                            }}
                            style={props.customSelectStyles}
                            tabIndex={props.tabIndex}
                            value={properties.value || ''}
                        >
                            {properties.placeholder && <option value={''}>{properties.placeholder}</option>}
                            {properties.selectOptions.map((option, i) => (
                                <option
                                    key={i}
                                    value={option.value}
                                >
                                    {option.text}
                                </option>
                            ))}
                        </select>
                        <Feedback
                            error={properties.error}
                            key="feedback"
                        />
                    </>
                );
            }}
        </FormContextConsumer>
    );
}

export function DropdownField<T extends string>(props: FormContextProps<IDropdownInputProps<T> & IFieldProps>) {
    return (
        <Field {...props}>
            <DropdownInput<T> {...props} />
        </Field>
    );
}
